import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { villageInfo, demographics, heroes, nearbyVillages, connectivity } from '../data/mock';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  MapPin, Users, Home, BookOpen, Bus, Train, Award,
  Map, Navigation, Building, ExternalLink
} from 'lucide-react';

const AboutPage = () => {
  const { t, getText, language } = useLanguage();

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Hero */}
      <section className="bg-gradient-to-r from-amber-500 to-orange-500 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              {t('about')} {getText(villageInfo.name)}
            </h1>
            <p className="text-amber-100 max-w-2xl mx-auto">
              {getText(villageInfo.tagline)}
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Basic Info */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="md:col-span-2 border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-amber-500" />
                {language === 'pa' ? 'ਸਥਿਤੀ ਤੇ ਜਾਣਕਾਰੀ' : 'Location & Details'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Building className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਤਹਿਸੀਲ' : 'Tehsil'}</p>
                      <p className="font-medium">{getText(villageInfo.tehsil)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Map className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਜ਼ਿਲ਼ਾ' : 'District'}</p>
                      <p className="font-medium">{getText(villageInfo.district)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Navigation className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਰਾਜ' : 'State'}</p>
                      <p className="font-medium">{getText(villageInfo.state)}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <MapPin className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਪਿਨ ਕੋਡ' : 'Pin Code'}</p>
                      <p className="font-medium">{villageInfo.pincode}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Home className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਖੇਤਰ' : 'Area'}</p>
                      <p className="font-medium">{villageInfo.area}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                    <Building className="h-5 w-5 text-amber-600" />
                    <div>
                      <p className="text-xs text-gray-500">{language === 'pa' ? 'ਨੇੜਲਾ ਸ਼ਹਿਰ' : 'Nearest Town'}</p>
                      <p className="font-medium">{getText(villageInfo.nearestTown)} ({villageInfo.distanceFromDistrict})</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-500 to-orange-500 text-white">
            <CardContent className="p-6 flex flex-col justify-center h-full">
              <h3 className="text-lg font-medium mb-2 opacity-90">{language === 'pa' ? 'ਪਿੰਡ ਕੋਡ' : 'Village Code'}</h3>
              <p className="text-4xl font-bold mb-4">{villageInfo.villageCode}</p>
              <p className="text-sm opacity-80">Census 2011</p>
            </CardContent>
          </Card>
        </div>

        {/* Demographics */}
        <Card className="border-0 shadow-lg mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-amber-500" />
              {t('demographics')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="text-center p-4 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl">
                <p className="text-3xl font-bold text-blue-600">{demographics.totalPopulation.toLocaleString()}</p>
                <p className="text-sm text-gray-500">{t('population')}</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl">
                <p className="text-3xl font-bold text-emerald-600">{demographics.households}</p>
                <p className="text-sm text-gray-500">{t('households')}</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl">
                <p className="text-3xl font-bold text-purple-600">{demographics.sexRatio}</p>
                <p className="text-sm text-gray-500">{t('sexRatio')}</p>
              </div>
              <div className="text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl">
                <p className="text-3xl font-bold text-amber-600">{demographics.literacyRate}%</p>
                <p className="text-sm text-gray-500">{t('literacyRate')}</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-3">{language === 'pa' ? 'ਆਬਾਦੀ ਵੇਰਵਾ' : 'Population Breakdown'}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t('male')}</span>
                    <span className="font-semibold">{demographics.male.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t('female')}</span>
                    <span className="font-semibold">{demographics.female.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{t('scheduledCastes')}</span>
                    <span className="font-semibold">{demographics.scheduledCastes.toLocaleString()}</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-medium text-gray-700 mb-3">{language === 'pa' ? 'ਸਾਖਰਤਾ ਵੇਰਵਾ' : 'Literacy Breakdown'}</h4>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{language === 'pa' ? 'ਮਰਦ ਸਾਖਰਤਾ' : 'Male Literacy'}</span>
                    <span className="font-semibold">{demographics.maleLiteracy}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{language === 'pa' ? 'ਔਰਤ ਸਾਖਰਤਾ' : 'Female Literacy'}</span>
                    <span className="font-semibold">{demographics.femaleLiteracy}%</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-gray-600">{language === 'pa' ? 'ਪੜ੍ਹੇ-ਲਿਖੇ' : 'Literate'}</span>
                    <span className="font-semibold">{demographics.literatePopulation.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Heroes */}
        <Card className="border-0 shadow-lg mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Award className="h-5 w-5 text-amber-500" />
              {t('heroes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {heroes.map((hero) => (
                <div key={hero.id} className="flex gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl">
                  <img 
                    src={hero.image} 
                    alt={getText(hero.name)}
                    className="w-24 h-32 object-cover object-top rounded-lg shadow-md"
                  />
                  <div>
                    <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded">{hero.year}</span>
                    <h3 className="font-bold text-gray-900 mt-2">{getText(hero.name)}</h3>
                    <p className="text-sm text-amber-600">{getText(hero.title)}</p>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-3">{getText(hero.description)}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Google Maps Location */}
        <Card className="border-0 shadow-lg mb-12">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Map className="h-5 w-5 text-amber-500" />
              {language === 'pa' ? 'ਪਿੰਡ ਦੀ ਸਥਿਤੀ' : 'Village Location'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="rounded-xl overflow-hidden shadow-md">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13543.84!2d74.92!3d31.45!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x391907d8a4c8c6e7%3A0x8a4c8a4c8a4c8a4c!2sMarhana%2C%20Punjab%20143410!5e0!3m2!1sen!2sin!4v1706000000000!5m2!1sen!2sin"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Marhana Village Location"
                className="w-full"
              ></iframe>
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
              <div>
                <p className="text-sm text-gray-500">
                  {language === 'pa' ? 'ਪਤਾ' : 'Address'}: {getText(villageInfo.name)}, {getText(villageInfo.tehsil)}, {getText(villageInfo.district)}, {getText(villageInfo.state)} - {villageInfo.pincode}
                </p>
                <p className="text-xs text-gray-400 mt-1">
                  {language === 'pa' ? 'ਕੋਆਰਡੀਨੇਟ' : 'Coordinates'}: 31.45°N, 74.92°E
                </p>
              </div>
              <a 
                href="https://www.google.com/maps/search/Marhana+Punjab+143410"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-sm font-medium"
              >
                <ExternalLink className="h-4 w-4" />
                {language === 'pa' ? 'Google Maps ਵਿੱਚ ਖੋਲ੍ਹੋ' : 'Open in Google Maps'}
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Connectivity & Nearby */}
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bus className="h-5 w-5 text-amber-500" />
                {t('connectivity')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                  <Bus className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">{language === 'pa' ? 'ਸਰਕਾਰੀ ਬੱਸ' : 'Public Bus'}</p>
                    <p className="font-medium">{getText(connectivity.publicBus)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                  <Bus className="h-5 w-5 text-emerald-600" />
                  <div>
                    <p className="text-sm text-gray-500">{language === 'pa' ? 'ਪ੍ਰਾਈਵੇਟ ਬੱਸ' : 'Private Bus'}</p>
                    <p className="font-medium">{getText(connectivity.privateBus)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                  <Train className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-sm text-gray-500">{language === 'pa' ? 'ਰੇਲਵੇ' : 'Railway'}</p>
                    <p className="font-medium">{getText(connectivity.railway)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-amber-500" />
                {t('nearbyVillages')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {nearbyVillages.map((village, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 bg-amber-50 text-amber-700 rounded-full text-sm font-medium hover:bg-amber-100 transition-colors cursor-default"
                  >
                    {village}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AboutPage;
